"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createConfigContext = exports.configContext = exports.defaultContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ab_1 = require("./ab");
const useUrlExperimentOverride_1 = require("./useUrlExperimentOverride");
const web_log_1 = require("@snapchat/web-log");
const useUrlConfigOverride_1 = require("./useUrlConfigOverride");
const web_sc_cookies_1 = require("@snapchat/web-sc-cookies");
const useExperiment_1 = require("./useExperiment");
const web_extraction_1 = require("@snapchat/web-extraction");
exports.defaultContext = {
    configs: [],
    isLoading: false,
    hasFetchedConfigs: false,
    userHasOptedOutOfExperiments: false,
    countryCode: "ZZ",
};
/**
 * We are exporting the instance of the config context so that it can be used in tests to
 * set up a particular environment by wrapping tests with a context component.
 */
exports.configContext = (0, react_1.createContext)(exports.defaultContext);
/**
 * createConfigContext will create utilities that are
 * scoped to a given namespace allowing consumers
 * to create multiple different contexts at various
 * levels of the application. See README.md for more details
 */
function createConfigContext(namespaces) {
    function Provider(props) {
        const configContextValue = (0, react_1.useContext)(exports.configContext);
        const { experimentNamespaces, webClientId, countryCode } = props;
        const [results, setResult] = (0, react_1.useState)(undefined);
        const [userHasOptedOutOfExperiments, setUserHasOptedOutOfExperiment] = (0, react_1.useState)(false);
        (0, react_1.useEffect)(() => {
            // run on the client-side, uses "document"
            setUserHasOptedOutOfExperiment((0, web_sc_cookies_1.userOptedOutOfEssentialCookies)() || (0, web_extraction_1.isTiktok)());
        }, []);
        (0, react_1.useEffect)(() => {
            let isMounted = true;
            // Exclude Tiktok browser from running AB since tiktok behaviour is broken
            // [WEB-2900] Re-Include Tiktok to AB scope when experience is fixed
            if (webClientId && !(0, web_extraction_1.isTiktok)()) {
                setResult((prevState) => ({
                    configs: [],
                    isLoading: true,
                    hasFetchedConfigs: prevState?.hasFetchedConfigs || false,
                }));
                // Setting hasFetchedConfigs to true in SB tests allows skipping fetchConfigs() below:
                if (!configContextValue.hasFetchedConfigs) {
                    (0, ab_1.fetchConfigs)({
                        webClientId,
                        namespaces: Array.from(new Set([...namespaces, ...(experimentNamespaces || [])])),
                    })
                        .then((configs) => {
                        if (isMounted) {
                            setResult({
                                isLoading: false,
                                configs,
                                hasFetchedConfigs: true,
                            });
                        }
                    })
                        .catch((e) => {
                        if (isMounted) {
                            web_log_1.log.error("Could not fetch configs:", e);
                            setResult({
                                configs: [],
                                isLoading: false,
                                hasFetchedConfigs: true,
                            });
                        }
                    });
                }
            }
            return () => {
                isMounted = false;
            };
        }, [countryCode, experimentNamespaces, webClientId, configContextValue.hasFetchedConfigs]);
        const resultValues = (0, react_1.useMemo)(() => {
            const resultsOrDefault = results ?? exports.defaultContext;
            return { ...resultsOrDefault, userHasOptedOutOfExperiments, countryCode };
        }, [results, userHasOptedOutOfExperiments, countryCode]);
        return (0, jsx_runtime_1.jsx)(exports.configContext.Provider, { value: resultValues, children: props.children });
    }
    function Experiment({ configId, webClientId, pageTypeName, configIdForSyncExposureLog = `${configId}_SYNC_LOGGING`, children, onLogExposure, }) {
        const { isLoading, configs: _configs, hasFetchedConfigs, userHasOptedOutOfExperiments, countryCode, } = (0, react_1.useContext)(exports.configContext);
        const configs = (0, react_1.useMemo)(() => _configs || [], [_configs]);
        const config = (0, useUrlConfigOverride_1.useUrlConfigOverride)(configId, configs);
        const experimentValue = (0, useUrlExperimentOverride_1.useUrlExperimentOverride)(configId, configs);
        const onLogExposureRef = (0, react_1.useRef)(onLogExposure);
        onLogExposureRef.current = onLogExposure;
        const [isExposureLogged, setIsExposureLogged] = (0, react_1.useState)(false);
        const shouldWaitForExposureLogging = useConfigId(configIdForSyncExposureLog)?.value?.boolValue;
        const defaultResult = children({
            treatmentValue: useExperiment_1.TreatmentValue.Control,
            isLoading: false,
            hasFetchedConfigs: false,
            userHasOptedOutOfExperiments: true,
        });
        const fetchedResult = children({
            treatmentValue: experimentValue,
            isLoading,
            hasFetchedConfigs,
            userHasOptedOutOfExperiments,
        });
        // Exclude Tiktok browser from running AB since tiktok behaviour is broken
        // [WEB-2900] Re-Include Tiktok to AB scope when experience is fixed
        (0, react_1.useEffect)(() => {
            if (!(0, web_extraction_1.isTiktok)() && webClientId && config) {
                const payload = { countryCode: countryCode };
                void (0, ab_1.logExposure)(config, webClientId, pageTypeName, payload).finally(() => {
                    setIsExposureLogged(true);
                    onLogExposureRef.current?.(experimentValue);
                });
            }
        }, [countryCode, config, webClientId, pageTypeName, experimentValue]);
        // Tiktok browser should always have control by default
        if ((0, web_extraction_1.isTiktok)()) {
            return defaultResult;
        }
        // Delay returning fetched config result until batchUpdateAbExposure is completed
        if (shouldWaitForExposureLogging) {
            if (isExposureLogged) {
                return fetchedResult;
            }
            else {
                return defaultResult;
            }
        }
        return fetchedResult;
    }
    function useConfigs() {
        const context = (0, react_1.useContext)(exports.configContext);
        return context.configs;
    }
    /**
     * Get the COF config for a particular configId
     */
    function useConfigId(configId) {
        const configs = useConfigs();
        return (0, useUrlConfigOverride_1.useUrlConfigOverride)(configId, configs ?? []);
    }
    /**
     * Custom Hook which can be used to render different UI based on treatmentValue
     */
    function useTreatment(configId) {
        const { configs: _configs } = (0, react_1.useContext)(exports.configContext);
        const configs = (0, react_1.useMemo)(() => _configs || [], [_configs]);
        return (0, useUrlExperimentOverride_1.useUrlExperimentOverride)(configId, configs);
    }
    return {
        Provider,
        useConfigs,
        useConfigId,
        Experiment,
        useTreatment,
    };
}
exports.createConfigContext = createConfigContext;
//# sourceMappingURL=ConfigProvider.js.map